import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Link,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  outlinedButtonSx,
  primaryButtonSx,
} from "../../../Organization/CodeSecurity/codeSecurityShared";
import {
  DOMAIN_STAGES,
  DOMAIN_STATUSES,
  customUrl,
  normalizeDomain,
  validateCustomDomain,
} from "../../data/publishing";
import { DnsRecord } from "../components/PublishDialog";
import { Section } from "./Section";

const Row = ({ label, help, children }) => {
  const theme = useTheme();
  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={{ xs: 1, sm: 2 }}
      alignItems={{ sm: "flex-start" }}
      sx={{ py: 1.5 }}
    >
      <Stack
        direction="row"
        spacing={0.75}
        alignItems="center"
        sx={{ width: { sm: 170 }, flexShrink: 0, minHeight: 36 }}
      >
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {label}
        </Typography>
        <Tooltip title={help} arrow>
          <InfoOutlinedIcon
            aria-label={help}
            sx={{ fontSize: 16, color: theme.customColors.icons.secondary }}
          />
        </Tooltip>
      </Stack>
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          minHeight: 36,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        {children}
      </Box>
    </Stack>
  );
};

const StatusPill = ({ status, label }) => {
  const theme = useTheme();
  const colors = {
    unknown: theme.customColors.text.disabled,
    pending: theme.customColors.status.warning.main,
    misconfigured: theme.customColors.status.error.main,
    active: theme.customColors.status.success.main,
  };
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 1,
        px: 1.5,
        py: 0.75,
        borderRadius: "8px",
        border: `1px solid ${theme.customColors.border.main}`,
        textTransform: "uppercase",
        fontSize: 13,
        fontWeight: 600,
        letterSpacing: 0.5,
        color: theme.customColors.text.secondary,
      }}
    >
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          backgroundColor: colors[status],
        }}
      />
      {label || DOMAIN_STATUSES[status].label}
    </Box>
  );
};

// "Custom domain" section of the Trust Center settings. Changes apply at once
// (no Save), like ComplyJet's Custom Domain card: the server saves the
// domain, looks its DNS record up and keeps the status. Each action
// resolves to true when it worked (the page shows the server's message
// otherwise).
const CustomDomainPanel = ({
  data,
  regoditLink,
  onConnect,
  onCheck,
  onRemove,
}) => {
  const theme = useTheme();
  const { publishing } = data;
  const domain = publishing.customDomain;
  const status = publishing.customDomainStatus || "unknown";
  const target = publishing.cnameTarget;
  // CloudFront progress (null when the API only checks DNS).
  const stage = DOMAIN_STAGES[publishing.customDomainStage] || null;
  const help =
    publishing.customDomainStage === "failed"
      ? publishing.customDomainError || stage.help
      : stage?.help || DOMAIN_STATUSES[status].help;
  const [input, setInput] = useState("");
  const [error, setError] = useState("");
  // "connect" | "check" | "remove" while that call runs.
  const [busy, setBusy] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => setConfirmDelete(false), [domain]);

  const run = async (what, action) => {
    setBusy(what);
    const ok = await action();
    setBusy(null);
    return ok;
  };

  const connect = async () => {
    const problem = validateCustomDomain(input, data.company?.domain || "");
    if (problem) {
      setError(problem);
      return;
    }
    if (await run("connect", () => onConnect(normalizeDomain(input)))) {
      setInput("");
      setError("");
    }
  };

  const checkStatus = () => run("check", onCheck);

  const remove = () => {
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    run("remove", onRemove);
  };

  return (
    <Section
      title="Custom domain"
      subtitle="Connect your own domain and open your Trust Center there."
    >
      <Box>
        <Row
          label="Base domain"
          help="Your Trust Center always works at this Regodit link, even with a custom domain."
        >
          <Link
            href={regoditLink}
            target="_blank"
            rel="noopener"
            variant="body2"
            sx={{ wordBreak: "break-all" }}
          >
            {regoditLink.replace(/^https?:\/\//, "")}
          </Link>
        </Row>

        <Row
          label="Custom domain"
          help="A subdomain your company owns, like trust.yourcompany.com."
        >
          {domain ? (
            <Link
              href={customUrl(domain)}
              target="_blank"
              rel="noopener"
              variant="body2"
              sx={{ wordBreak: "break-all" }}
            >
              {domain}
            </Link>
          ) : (
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <TextField
                size="small"
                fullWidth
                label="Your domain"
                placeholder="trust.yourcompany.com"
                value={input}
                onChange={(e) => {
                  setInput(e.target.value);
                  setError("");
                }}
                onKeyDown={(e) => e.key === "Enter" && connect()}
                error={!!error}
                helperText={error}
              />
              <Button
                variant="contained"
                onClick={connect}
                disabled={busy === "connect"}
                sx={{ ...primaryButtonSx(theme), py: 0.9, flexShrink: 0 }}
              >
                {busy === "connect" ? "Connecting…" : "Connect"}
              </Button>
            </Stack>
          )}
        </Row>

        {domain && (
          <>
            <Row
              label="Status"
              help="Whether we found the DNS record that points your domain to us."
            >
              <Stack
                direction="row"
                spacing={1.5}
                alignItems="center"
                sx={{ flexWrap: "wrap", rowGap: 1 }}
              >
                <StatusPill status={status} label={stage?.label} />
                <Button
                  variant="outlined"
                  onClick={checkStatus}
                  disabled={!!busy}
                  startIcon={
                    busy === "check" ? (
                      <CircularProgress size={14} color="inherit" />
                    ) : null
                  }
                  sx={outlinedButtonSx(theme)}
                >
                  {busy === "check" ? "Checking…" : "Check status"}
                </Button>
                {publishing.customDomainCheckedAt && (
                  <Typography
                    variant="caption"
                    sx={{ color: theme.customColors.text.secondary }}
                  >
                    Checked{" "}
                    {new Date(publishing.customDomainCheckedAt).toLocaleString(
                      undefined,
                      { dateStyle: "medium", timeStyle: "short" }
                    )}
                  </Typography>
                )}
              </Stack>
              <Typography
                variant="caption"
                sx={{
                  mt: 0.75,
                  color:
                    status === "misconfigured"
                      ? theme.customColors.status.error.text
                      : theme.customColors.text.secondary,
                }}
              >
                {help}
                {status !== "active" && publishing.customDomainStage && (
                  <> We check again every few minutes and email you when it is live.</>
                )}
              </Typography>
              {status !== "active" && (
                <>
                  <DnsRecord domain={domain} target={target} />
                  <Box
                    component="ul"
                    aria-label="DNS tips"
                    sx={{
                      m: 0,
                      mt: 1,
                      pl: 2.5,
                      color: theme.customColors.text.secondary,
                    }}
                  >
                    <Typography component="li" variant="caption">
                      Using Cloudflare? Set this record to DNS only (grey
                      cloud), not proxied.
                    </Typography>
                    <Typography component="li" variant="caption">
                      If your domain has CAA records, add one that allows
                      amazon.com, so the certificate can be issued.
                    </Typography>
                  </Box>
                </>
              )}
            </Row>

            <Row
              label="Actions"
              help="Removing the custom domain keeps your Trust Center live at the base domain."
            >
              <Box>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteOutlineIcon />}
                  onClick={remove}
                  disabled={busy === "remove"}
                  sx={{ borderRadius: "8px", textTransform: "none" }}
                >
                  {confirmDelete ? "Yes, delete it" : "Delete custom domain"}
                </Button>
                {confirmDelete && (
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      mt: 0.75,
                      fontStyle: "italic",
                      color: theme.customColors.text.secondary,
                    }}
                  >
                    Click again to delete. Your page stays live at the base
                    domain.
                  </Typography>
                )}
              </Box>
            </Row>
          </>
        )}
      </Box>
    </Section>
  );
};

export default CustomDomainPanel;
